import { i18n } from '@/i18n.js'
/** Durable, identity-scoped operations. Executors must accept a stable replay key. */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { useAuthStore } from '@/stores/auth'

const STORE = 'operations'
let database
let flushing = false
const executors = new Map()
async function getDb() {
  if (!database) database = await new Promise((resolve, reject) => {
    const req = indexedDB.open('eqsys_offline_queue', 1)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
      }
    }
    req.onsuccess = e => resolve(e.target.result)
    req.onerror = () => reject(req.error)
  })
  return database
}
function operation(db, method, value, writable = false) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, writable ? 'readwrite' : 'readonly')
    const req = tx.objectStore(STORE)[method](value)
    // A successful request can still be rolled back by its transaction.
    tx.oncomplete = () => resolve(req.result)
    tx.onerror = () => reject(tx.error ?? req.error)
    tx.onabort = () => reject(tx.error ?? new Error('Storage transaction aborted.'))
    req.onerror = () => reject(req.error)
  })
}
export function registerExecutor(type, fn) { executors.set(type, fn) }

export function useOfflineQueue() {
  const auth = useAuthStore()
  const scope = computed(() => auth.currentTenant?.id && auth.user?.id
    ? String(auth.currentTenant.id) + ':' + String(auth.user.id) : null)
  const pendingCount = ref(0)
  const failedCount = ref(0)
  const error = ref(null)
  const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
  async function records() {
    const all = await operation(await getDb(), 'getAll')
    return (all ?? []).filter(op => scope.value && op.scope === scope.value).sort((a,b) => a.id - b.id)
  }
  async function refresh() {
    try {
      const ops = await records()
      pendingCount.value = ops.length
      failedCount.value = ops.filter(op => op.retries >= 3 || !executors.has(op.type)).length
    } catch (e) { error.value = e.message }
  }
  async function enqueue(type, payload, executor) {
    if (!scope.value) throw new Error(i18n.global.t('ui.signInAndSelectATenantBeforeSavingAnOperation'))
    if (isOnline.value) return executor()
    await operation(await getDb(), 'add', {
      type, payload, scope: scope.value, key: uuidv4(), createdAt: Date.now(), retries: 0,
    }, true)
    await refresh()
  }
  async function flush() {
    if (flushing || !isOnline.value || !scope.value) return
    flushing = true
    const startedScope = scope.value
    try {
      const db = await getDb()
      for (const op of await records()) {
        if (scope.value !== startedScope || !isOnline.value) break
        const execute = executors.get(op.type)
        // Retain unknown and exhausted operations for explicit recovery.
        if (!execute || op.retries >= 3) break
        try {
          await execute(op.payload, op.key)
          await operation(db, 'delete', op.id, true)
        } catch (e) {
          op.retries = (op.retries ?? 0) + 1
          op.lastError = e.message
          await operation(db, 'put', op, true)
          error.value = e.message
          break
        }
      }
    } catch (e) { error.value = e.message }
    finally { flushing = false; await refresh() }
  }
  async function retry(id) {
    const op = (await records()).find(op => op.id === id)
    if (!op) return
    op.retries = 0
    delete op.lastError
    await operation(await getDb(), 'put', op, true)
    await flush()
  }
  function online() { isOnline.value = true; flush() }
  function offline() { isOnline.value = false }
  watch(scope, () => { refresh(); if (isOnline.value) flush() })
  onMounted(() => {
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    refresh()
    if (isOnline.value) flush()
  })
  onUnmounted(() => {
    window.removeEventListener('online', online)
    window.removeEventListener('offline', offline)
  })
  return { pendingCount, failedCount, error, isOnline, enqueue, flush, retry }
}
